'use client';

import { Facebook, Instagram, Mail } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { signOut, useSession } from 'next-auth/react';
import { useState } from 'react';

import { AppConfig } from '@/utils/AppConfig';

import SignInOrRegisterModal from '../modals/SignInOrRegisterModal';
import { Button } from '../ui/button';

const quickLinks = [
  { href: '/used-cars', label: '瀏覽二手車' },
  { href: '/services', label: '車週邊服務' },
  { href: '/license-plate-preview', label: '自訂車牌' },
  { href: '/articles', label: '資訊中心' },
];

const aboutLinks = [
  { href: '/about/our-story', label: '我們的故事' },
  { href: '/about', label: '關於我們' },
  { href: '/business', label: '商業招募計畫' },
  { href: '/contact', label: '聯絡我們' },
];

const supportLinks = [
  { href: '/help', label: '幫助中心' },
  { href: '/faq', label: '常見問題' },
  { href: '/privacy', label: '私隱政策' },
  { href: '/terms', label: '條款及細則' },
];

const Footer = () => {
  const { data: session } = useSession();
  const [openSignInOrRegisterModal, setOpenSignInOrRegisterModal] =
    useState<boolean>(false);
  const isAuthenticated = !!session?.user;

  return (
    <footer className="border-t border-gray-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-12">
          {/* Logo and Contact Section */}
          <div className="lg:col-span-4">
            <div className="flex items-center">
              <Image
                src="/logo.svg"
                width={48}
                height={48}
                alt="PairCar Logo"
                className="h-12 w-12"
              />
              <span className="ml-3 text-xl font-bold text-gray-900">
                PairCar
              </span>
            </div>
            <p className="mt-4 text-sm leading-6 text-gray-600">
              香港最大的二手車交易平台，為您提供全面的汽車服務。
            </p>
            <div className="mt-6 space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <Mail className="mr-2 h-5 w-5 text-gray-400" />
                <a
                  href="mailto:contact@paircar.com"
                  className="hover:text-blue-600"
                >
                  contact@paircar.com
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900">快速連結</h3>
            <ul className="mt-4 space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-gray-600 transition-colors hover:text-blue-600"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                {isAuthenticated ? (
                  <Link
                    href="/dashboard/upload-car"
                    className="text-sm text-gray-600 transition-colors hover:text-blue-600"
                  >
                    賣車
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => setOpenSignInOrRegisterModal(true)}
                    className="text-sm text-gray-600 transition-colors hover:text-blue-600"
                  >
                    賣車
                  </button>
                )}
              </li>
            </ul>
          </div>

          {/* About Links */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900">關於PairCar</h3>
            <ul className="mt-4 space-y-3">
              {aboutLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-gray-600 transition-colors hover:text-blue-600"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900">支援</h3>
            <ul className="mt-4 space-y-3">
              {supportLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-gray-600 transition-colors hover:text-blue-600"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 border-t border-gray-200 pt-8">
          <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} {AppConfig.site_name}. All rights
              reserved.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/profile.php?id=61564051015790"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-gray-100 p-2 text-gray-500 transition-colors hover:bg-gray-200"
              >
                <Facebook className="h-5 w-5" />
                <span className="sr-only">Facebook</span>
              </a>
              <a
                href="https://www.instagram.com/paircar.official"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-gray-100 p-2 text-gray-500 transition-colors hover:bg-gray-200"
              >
                <Instagram className="h-5 w-5" />
                <span className="sr-only">Instagram</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <SignInOrRegisterModal
        isOpen={openSignInOrRegisterModal}
        setIsOpen={setOpenSignInOrRegisterModal}
      />
    </footer>
  );
};

export default Footer;
